import React, { useEffect } from 'react'
import { RouteComponentProps, Link } from 'react-router-dom'
import classNames from 'classnames'
import {
  UniversalHeader as UniversalHeaderLegacy,
  Card,
  CallToAction,
  Paragraph,
  Row,
} from '@scentregroup/ui'
import { useCountryContext, useEvolvedContext } from '../../context'
import { getCountry } from '../../helpers/get-country'
import { titleFromContentPage } from '../../helpers/title-from-content-page'
import Modules from '../../components/modules'
import Footer from '../../components/footer'
import { UniversalFooter } from '../../components/universal-footer'
import ExternalLink from '../../components/external-link'
import PageLanding from '../../components/page-landing'
import Article from '../../components/article'
import withContentPage from '../../containers/with-content-page'
import {
  MetaData as ContentPageMetaData,
  NavigationGroup,
  NavigationItem,
  PromotionFeature,
  Promotion,
  isPromotionFeature,
  ComparisonModule,
  ContentPageDisplayModes,
  isDisplayMode,
} from '../../../types/content-page'
import { ThemesColour } from '../../../types/themes-colour'
import { MetaData } from '../../../types/meta-data'
import pagesHoc from '../pages-hoc'
import { actions, categories } from '../../helpers/analytics-utils/constants'
import analyticsUtils from '../../helpers/analytics-utils'
import { createProductClickEvent } from '../../helpers/analytics-utils/productClick'
import trackEvent from '../../helpers/analytics-utils/track-event'
import {
  GiftCardsStaticData,
  useUniversalHeader,
} from '../../hub-components/universal-header'
import style from './index.module.scss'

interface PromotionalPageProps {
  modules?: (PromotionFeature | Promotion | ComparisonModule)[]
  metaData?: ContentPageMetaData
  navigationMenu?:
    | (NavigationItem | NavigationGroup)[]
    | {
        nav: (NavigationItem | NavigationGroup)[]
      }
  loading?: boolean
  centres?: any
  nationalNavLinks?: any
  headerStaticData: GiftCardsStaticData
}

//  Temporary URL
const footerLinks = [
  {
    url: 'https://support.westfield.com.au/hc/en-us/requests/new',
    label: 'Contact us',
  },
  {
    url:
      'https://support.westfield.com.au/hc/en-us/sections/360007624151-Digital-Gift-Cards',
    label: 'Frequently asked questions',
  },
  {
    url:
      'https://westfield.com.au/terms-and-conditions#gift-card-product-terms-and-conditions',
    label: 'Gift Card Terms & Conditions',
  },
  {
    url:
      'https://westfield.com.au/terms-and-conditions#digital-gift-card-product-terms-and-conditions',
    label: 'Digital Gift Card Terms & Conditions',
  },
]

const getProductDetailEvent = (modules: ComparisonModule): void => {
  analyticsUtils.gaTagTracking({
    event: analyticsUtils.events.PRODUCT_IMPRESSION,
    ecommerce: {
      currencyCode: 'AUD',
      impressions: modules
        ? modules.entries.map((item, index) => ({
            name: item.productName,
            id: `card100${index + 1}`,
            category: item.productName,
            brand: 'westfield',
            position: index + 1,
            list: item.productName,
          }))
        : [],
    },
  })
}

const PromotionalPage: React.FunctionComponent<
  RouteComponentProps & PromotionalPageProps
> = ({ modules, metaData, navigationMenu, headerStaticData }): JSX.Element => {
  // Support the new giftcard nav JSON structure
  let safeNavigationMenu = navigationMenu
  if (
    navigationMenu &&
    !Array.isArray(navigationMenu) &&
    navigationMenu.nav &&
    Array.isArray(navigationMenu.nav)
  ) {
    safeNavigationMenu = navigationMenu.nav
  }

  const country = useCountryContext()
  const isEvolved = useEvolvedContext()
  const [Header, setHeaderNavigationPath] = useUniversalHeader({
    variant: 'gift-cards',
    data: headerStaticData,
  })

  const pageTitleLink = (
    <Link
      to={metaData ? metaData.path : '/'}
      title={
        metaData ? `Go to ${metaData.title}` : `Go to Westfield ${country}`
      }
      onClick={() => {
        analyticsUtils.gaTagTracking({
          event: analyticsUtils.events.USER_CLICK,
          eventCategory: metaData ? metaData.title : undefined,
          eventAction: analyticsUtils.actions.GIFTCARD_HEADER_CLICK,
        })
      }}
    />
  )

  const comparisonModule =
    modules &&
    (modules.find(({ displayMode }) =>
      isDisplayMode(displayMode, ContentPageDisplayModes.COMPARISON_ITEM)
    ) as ComparisonModule)

  useEffect(() => {
    if (comparisonModule) {
      getProductDetailEvent(comparisonModule)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (modules) {
    const firstModule =
      Boolean(modules?.length) &&
      isPromotionFeature(modules[0]) &&
      (modules[0] as PromotionFeature)

    const restModules = firstModule ? modules.slice(1) : modules

    const dgcComparisonItem =
      comparisonModule &&
      comparisonModule.entries.find(
        ({ productName }) =>
          productName === analyticsUtils.categories.DIGITAL_GIFT_CARD
      )
    const cartLink = (
      <ExternalLink
        url={'/shop/westfield-digital-gift-card/cart'}
        {...{ rel: 'noopener', target: '_self' }}
        eventCategory={metaData ? metaData.title : undefined}
        eventAction={analyticsUtils.actions.GIFTCARD_CART_CLICK}
      />
    )

    const createNavLinks = (item: {
      url: string
      label: string
    }): JSX.Element => (
      <ExternalLink
        {...item}
        {...{ rel: 'noopener', target: '_self' }}
        onClick={() =>
          trackEvent(
            metaData ? metaData.title : undefined,
            analyticsUtils.actions.GIFTCARD_NAV_CLICK,
            item.label
          )
        }
      />
    )
    return (
      <div className={classNames({ [style.evolved]: isEvolved })}>
        {isEvolved ? (
          <Header />
        ) : (
          navigationMenu && (
            <UniversalHeaderLegacy
              pageTitle={metaData && metaData.title}
              navigationMenu={safeNavigationMenu}
              createLinkElement={createNavLinks}
              cartLink={cartLink}
              pageTitleLink={pageTitleLink}
              colour={ThemesColour.BRAND}
            />
          )
        )}
        {/* explicitly sending undefined props for ts vs js reasons */}
        {firstModule && (
          <PageLanding
            desktopImage={{ src: firstModule.desktopImageUrl }}
            mobileImage={{ src: firstModule.mobileImageUrl }}
            logo={undefined}
            heading={undefined}
            dataTestId={undefined}
          >
            <Card className={style.card}>
              <Article heading={firstModule.title}>
                <Paragraph>{firstModule.body}</Paragraph>
                <Row className={style.callToActionWrapper}>
                  <CallToAction
                    {...{ rel: 'noopener', target: '_self' }}
                    inverted
                    inferior
                    url={firstModule.callToAction.address}
                    element={ExternalLink}
                    eventCategory={metaData ? metaData.title : undefined}
                    eventAction={analyticsUtils.categories.FEATURE_CTA}
                    eventLabel={firstModule.callToAction.category}
                  >
                    {firstModule.callToAction.category}
                  </CallToAction>

                  {dgcComparisonItem ? (
                    <CallToAction
                      {...{ rel: 'noopener', target: '_self' }}
                      url={dgcComparisonItem.callToAction.url}
                      element={ExternalLink}
                      onClick={() =>
                        createProductClickEvent(
                          dgcComparisonItem.productName,
                          analyticsUtils.categories.FEATURE_CTA
                        )
                      }
                    >
                      {dgcComparisonItem.callToAction.category}
                    </CallToAction>
                  ) : (
                    <div />
                  )}
                </Row>
              </Article>
            </Card>
          </PageLanding>
        )}

        <Modules
          modules={restModules}
          className={style.initialContentGroup}
          initialAngle="top-right"
          centreTitle={undefined}
        />
        {isEvolved ? (
          <UniversalFooter
            Link={Link}
            country={country}
            onFindACentre={() => setHeaderNavigationPath(['find-a-centre'])}
          />
        ) : (
          <Footer colour={ThemesColour.BRAND}>
            <div className={style.contentWrapper}>
              <div className={style.title}>
                Westfield {metaData && metaData.title}
              </div>
              {footerLinks &&
                footerLinks.map(item => (
                  <ExternalLink
                    {...{ rel: 'noopener', target: '_self' }}
                    key={item.label}
                    url={item.url}
                    className={style.link}
                    eventCategory={categories.GIFTCARD_CLICK}
                    eventLabel={item.label}
                    eventAction={actions.GIFTCARD_FOOTER_CLICK}
                  >
                    {item.label}
                  </ExternalLink>
                ))}
            </div>
          </Footer>
        )}
      </div>
    )
  } else {
    return <div />
  }
}

const getMetaData = ({
  metaData,
  staticContext,
}: RouteComponentProps & PromotionalPageProps): MetaData => {
  const COUNTRY = getCountry(staticContext)
  return {
    title: titleFromContentPage(COUNTRY, metaData),
    description: metaData?.shortDescription,
  }
}

export default withContentPage(pagesHoc(PromotionalPage, getMetaData)) as any
